import { Dispatch, FC, SetStateAction, useCallback } from "react";
import { Button, Dropdown, message, useMessagedAsyncFn } from "@payping/web-components";
import { DownOutlined } from "@payping/icons";
import { InvoiceStatusEnum, invoiceV2Apis, uploadV2Apis } from "@payping/api";
import type { InvoiceComplexDetailViewModel } from "@payping/api";
import { cancelInvoice, reminderInvoice } from "@payping/redux/actions";
import { generatePath, useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import type { MenuProps } from "@payping/web-components/components/menu";
import qs from "query-string";

interface Props {
  data?: InvoiceComplexDetailViewModel;
  setManualPaymentNotesModalVisible: Dispatch<SetStateAction<boolean>>;
  loading?: boolean;
  locationSearch?: string;
  retry: () => void;
  retryList: () => void;
  modal: any;
  code: string;
}

const DetailOperations: FC<Props> = (props: Props) => {
  const { code, data, setManualPaymentNotesModalVisible, loading, locationSearch, modal, retry, retryList } = props;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [, invoiceCancellationHandler] = useMessagedAsyncFn(
    async (schedule: boolean) => {
      if (code) await dispatch(cancelInvoice(code, schedule));
    },
    [code, dispatch],
  );

  const handleCancel = useCallback(() => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("invoice.confirm.cancelRegularInvoice"),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => invoiceCancellationHandler(false),
    });
  }, [invoiceCancellationHandler, modal, t]);

  const handleCancelSchedule = useCallback(() => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("invoice.confirm.cancelScheduleInvoice"),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => invoiceCancellationHandler(true),
    });
  }, [invoiceCancellationHandler, modal, t]);

  const [, handleRemind] = useMessagedAsyncFn(async () => {
    await dispatch(reminderInvoice(code));
  }, [code, dispatch]);
  const [, handleArchive] = useMessagedAsyncFn(async () => {
    if (data?.code) {
      if (!data?.archivedDateTime) {
        modal.confirm({
          cancelText: t("cancel"),
          content: t("invoice.archive.confirm"),
          mask: { closable: true },
          okText: t("ok"),
          onOk: async () => {
            if (data.code) {
              await invoiceV2Apis.v2InvoiceArchiveByCodePOST(data.code);
              message.success({ content: t("invoice.detail.archived") });
              retry();
              retryList();
            }
          },
        });
      } else {
        await invoiceV2Apis.v2InvoiceDeArchiveByCodePOST(data.code);
        message.success({ content: t("invoice.detail.restored") });
        retry();
        retryList();
      }
    }
  }, [data?.archivedDateTime, data?.code, modal, retry, t, retryList]);

  const [, handleOfficialInvoice] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      const response = await uploadV2Apis.generateOfficialInvoiceWithInvoiceCodeGet(code);
      newWindow?.location?.assign?.(response);
    },
    [code],
    undefined,
    { useProgress: true },
  );
  const [, handleReturnInvoice] = useMessagedAsyncFn(
    async () => {
      await invoiceV2Apis.v2InvoiceReturnPOST({ code });
      navigate(DashboardRoutes.myShop.invoice.root.url);
    },
    [code, navigate],
    undefined,
    { useProgress: true },
  );
  const [, handlePDF] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      const key = Math.random().toString();
      message.loading({ content: t("invoice.detail.makingPDF"), key });
      const resp = await uploadV2Apis.generateUnOfficialInvoiceWithInvoiceCodeGet(code);
      message.success({ content: t("invoice.detail.done"), duration: 1, key });
      newWindow?.location?.assign?.(resp);
    },
    [code, t],
  );

  const copyInvoice = () => {
    navigate(DashboardRoutes.myShop.invoice.create.url, { state: data });
  };

  const goToCorrection = () => {
    navigate(
      {
        pathname: DashboardRoutes.myShop.invoice.correction.url.replace(":code", code),
      },
      { state: { locationSearch } },
    );
  };

  const goToEdit = () => {
    navigate({ pathname: generatePath(DashboardRoutes.myShop.invoice.edit.url, { code }) }, { state: locationSearch });
  };

  const menuItems: MenuProps["items"] = [];
  if (data?.status === InvoiceStatusEnum.PAID) {
    menuItems.push(
      {
        key: "0",
        onClick: () => {
          const newWindow = window.open(undefined, "_blank");
          handleOfficialInvoice(newWindow);
        },
        label: t("invoice.officialInvoice.title"),
      },
      {
        key: "1",
        onClick: handleReturnInvoice,
        label: t("invoice.returnedInvoice"),
      },
    );
  }
  if (data?.status === InvoiceStatusEnum.DRAFT) {
    menuItems.push({
      key: "2",
      onClick: goToEdit,
      label: t("edit"),
    });
  }
  if (data?.status !== InvoiceStatusEnum.PAID && data?.status !== InvoiceStatusEnum.DRAFT) {
    menuItems.push({
      key: "3",
      onClick: () => {
        setManualPaymentNotesModalVisible(true);
      },
      label: t("invoice.detail.cashPayment"),
    });
  }
  if (
    data?.status !== InvoiceStatusEnum.CANCELED &&
    data?.status !== InvoiceStatusEnum.PAID &&
    data?.status !== InvoiceStatusEnum.DRAFT
  ) {
    menuItems.push({
      key: "4",
      onClick: handleRemind,
      label: t("reminder"),
    });
  }
  menuItems.push({
    key: "5",
    onClick: handleArchive,
    label: data?.archivedDateTime ? t("unArchive") : t("archive"),
  });
  if (data?.status === InvoiceStatusEnum.PAID) {
    menuItems.push({
      key: "6",
      onClick: goToCorrection,
      label: t("correction"),
    });
  }
  if (data?.status === InvoiceStatusEnum.AWAITING_PAYMENT || data?.status === InvoiceStatusEnum.DELAYED) {
    menuItems.push({ key: "7", onClick: handleCancel, label: t("cancel") });
  }
  menuItems.push({ key: "8", onClick: copyInvoice, label: t("copy") });
  if (data?.paymentCode) {
    menuItems.push({
      key: "9",
      onClick: () => {
        navigate({
          pathname: DashboardRoutes.mySale.report.url,
          search: qs.stringify({ searchVariant: "code", code: data.paymentCode }),
        });
      },
      label: t("invoice.transactionDetails"),
    });
  }
  menuItems.push({
    key: "10",
    onClick: () => {
      const newWindow = window.open(undefined, "_blank");
      handlePDF(newWindow);
    },
    label: t("pdf"),
  });
  if (data?.isSchedule) {
    menuItems.push({ key: "11", onClick: handleCancelSchedule, label: t("invoice.detail.cancelScheduling") });
  }

  return (
    <Dropdown menu={{ items: menuItems }}>
      <Button loading={loading} type="primary" ghost icon={<DownOutlined width={14} height={14} />} iconPosition="end">
        {t("operation")}
      </Button>
    </Dropdown>
  );
};

export default DetailOperations;
